import React, { useState } from 'react';
import { Play } from 'lucide-react';
import MultiSelectTicker from '../../MultiSelectTicker';
import AccordionFormSection from '../../Screener/AccordionFormSection';
import IntermediatePresetCard from '../../Screener/IntermediatePresetCard';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { actionButtonTemplate } from '../../../theme';
import {
  EXPIRY_SHAPES,
  MAX_DISTANCES,
  MAX_TENORS,
  SHAPE_EVEN,
  STRIKE_DISTANCES,
  STRIKE_SHAPES,
  TENORS,
  blockedReason,
  buildStraddlePayload,
  countStraddles,
  describeRun,
  describeSplits,
  resolveBudgets,
  selectedPairs,
  summarizeSizing,
  toggleCell,
  toggleShape,
} from '../../../utils/straddleMappings';
import StraddlePairMatrix from './StraddlePairMatrix';
import StraddleSizingControls from './StraddleSizingControls';

const DISTANCE_LABELS = new Map(STRIKE_DISTANCES.map((d) => [d.value, d.label]));
const distanceLabel = (value) => DISTANCE_LABELS.get(value);
const tenorLabel = (key) => key;

/** One pair grid's selection: the chips that are on, its shapes and any one-off clicks. */
const useAxis = (allValues, maxValues) => {
  const [chips, setChips] = useState([]);
  const [shapes, setShapes] = useState(() => new Set([SHAPE_EVEN]));
  const [overrides, setOverrides] = useState({});

  // Always nearest to farthest, whatever order the chips were clicked in, since a
  // cell's shape comes from its position on this axis.
  const axis = allValues.filter((value) => chips.includes(value));

  return {
    axis,
    shapes,
    overrides,
    pairs: selectedPairs(axis, shapes, overrides),
    atLimit: chips.length >= maxValues,
    toggleChip: (value) => setChips((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      return prev.length >= maxValues ? prev : [...prev, value];
    }),
    onToggleShape: (shape) => {
      const next = toggleShape(axis, shapes, overrides, shape);
      setShapes(next.shapes);
      setOverrides(next.overrides);
    },
    onToggleCell: (callValue, putValue) => {
      setOverrides(toggleCell(axis, shapes, overrides, callValue, putValue));
    },
  };
};

const pairSummary = (count) => (count === 0 ? '' : `${count} pair${count === 1 ? '' : 's'}`);

/**
 * Parameter form for the Straddle Simulator.
 *
 * Same accordion style as the Spread Simulator's form. The difference is the two
 * pair grids: each leg has its own strike distance and expiration, and crossing
 * those freely explodes, so the user picks the values and then the pairings.
 */
function StraddleForm({ onRun, loading, sizing, onSizingChange, hiddenBudgets }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [expandedSections, setExpandedSections] = useState(
    isMobile ? ['tickers'] : ['tickers', 'strikes', 'expirations', 'position']
  );

  const strikes = useAxis(STRIKE_DISTANCES.map((d) => d.value), MAX_DISTANCES);
  const expirations = useAxis(TENORS.map((t) => t.key), MAX_TENORS);

  const tickerCount = selectedTickers.length;
  const blocked = blockedReason(tickerCount, strikes.pairs.length, expirations.pairs.length);
  const budgets = resolveBudgets(sizing);
  const splitsSentence = describeSplits(
    countStraddles(tickerCount, strikes.pairs.length, expirations.pairs.length),
    budgets.length,
  );

  const handleToggle = (section) => {
    setExpandedSections((prev) => {
      if (isMobile) return prev.includes(section) ? [] : [section];
      return prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section];
    });
  };

  const handleRun = () => {
    if (blocked || !onRun) return;
    onRun(buildStraddlePayload(selectedTickers, strikes.pairs, expirations.pairs));
  };

  return (
    <div className="straddle-form">
      <AccordionFormSection
        title="Select Tickers"
        icon=""
        summary={selectedTickers.map((t) => t.value).join(', ')}
        isExpanded={expandedSections.includes('tickers')}
        isCompleted={tickerCount > 0}
        onToggle={() => handleToggle('tickers')}
        isMobile={isMobile}
      >
        <MultiSelectTicker
          value={selectedTickers}
          onChange={(tickers) => setSelectedTickers(tickers || [])}
          placeholder="Search and select tickers (e.g., AAPL, MSFT, NVDA)..."
          controlBackground="#1f2937"
        />
      </AccordionFormSection>

      <AccordionFormSection
        title="Strike Distance"
        icon=""
        summary={pairSummary(strikes.pairs.length)}
        isExpanded={expandedSections.includes('strikes')}
        isCompleted={strikes.pairs.length > 0}
        onToggle={() => handleToggle('strikes')}
        isMobile={isMobile}
      >
        <p className="section-helper-text">
          How far each leg sits from the stock price: the call above it, the put below it.
          {strikes.atLimit && ` You can pick up to ${MAX_DISTANCES}.`}
        </p>
        <div className="intermediate-preset-grid">
          {STRIKE_DISTANCES.map((distance) => (
            <IntermediatePresetCard
              key={distance.value}
              presetKey={distance.value}
              label={distance.label}
              description={distance.value === 0 ? 'At the money' : 'Out of the money'}
              isSelected={strikes.axis.includes(distance.value)}
              onClick={strikes.toggleChip}
            />
          ))}
        </div>
        <StraddlePairMatrix
          axis={strikes.axis}
          labelFor={distanceLabel}
          shapes={strikes.shapes}
          overrides={strikes.overrides}
          shapeOptions={STRIKE_SHAPES}
          onToggleShape={strikes.onToggleShape}
          onToggleCell={strikes.onToggleCell}
          callAxisTitle="Call"
          putAxisTitle="Put distance"
        />
      </AccordionFormSection>

      <AccordionFormSection
        title="Expiration"
        icon=""
        summary={pairSummary(expirations.pairs.length)}
        isExpanded={expandedSections.includes('expirations')}
        isCompleted={expirations.pairs.length > 0}
        onToggle={() => handleToggle('expirations')}
        isMobile={isMobile}
      >
        <p className="section-helper-text">
          Each leg can have its own expiration, such as a 1-month call with a 2-week put.
          Each choice uses a date listed in its range, preferring the monthly and Friday
          expirations that trade the most, so results show the exact date.
          {expirations.atLimit && ` You can pick up to ${MAX_TENORS}.`}
        </p>
        <div className="intermediate-preset-grid">
          {TENORS.map((tenor) => (
            <IntermediatePresetCard
              key={tenor.key}
              presetKey={tenor.key}
              label={tenor.label}
              description={`${tenor.min}-${tenor.max} days`}
              isSelected={expirations.axis.includes(tenor.key)}
              onClick={expirations.toggleChip}
            />
          ))}
        </div>
        <StraddlePairMatrix
          axis={expirations.axis}
          labelFor={tenorLabel}
          shapes={expirations.shapes}
          overrides={expirations.overrides}
          shapeOptions={EXPIRY_SHAPES}
          onToggleShape={expirations.onToggleShape}
          onToggleCell={expirations.onToggleCell}
          callAxisTitle="Call"
          putAxisTitle="Put expiration"
        />
      </AccordionFormSection>

      <AccordionFormSection
        title="Position Size"
        icon=""
        summary={summarizeSizing(sizing)}
        isExpanded={expandedSections.includes('position')}
        isCompleted={budgets.length > 0}
        onToggle={() => handleToggle('position')}
        isMobile={isMobile}
      >
        <StraddleSizingControls sizing={sizing} onChange={onSizingChange} hiddenBudgets={hiddenBudgets} />
      </AccordionFormSection>

      <div className={isMobile ? 'fixed-bottom-button' : 'inline-button'}>
        <p className="text-xs text-gray-400 mb-2 text-center" aria-live="polite">
          {describeRun(tickerCount, strikes.pairs.length, expirations.pairs.length)}
          {splitsSentence && <span className="block mt-1">{splitsSentence}</span>}
          {blocked && <span className="block text-yellow-400 mt-1">{blocked}</span>}
        </p>
        <button
          type="button"
          onClick={handleRun}
          disabled={Boolean(blocked) || loading}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} w-full ${blocked ? actionButtonTemplate.cssClasses.disabled : ''}`}
          title="Find these straddles"
        >
          {loading ? (
            <span className="spinner" />
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Play size={14} style={{ fill: 'currentColor' }} />
              Simulate Straddles
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

export default StraddleForm;
